import type { CharacterMemory } from './memoryTypes';
import type { CharacterOperation } from './operationTypes';
import type {
  InteractionLevel,
  MarketingDecision,
  TrustLevel,
} from './marketingTypes';

export interface MarketingDecisionInput {
  strategy?: unknown;
  memory: CharacterMemory;
  operation: CharacterOperation;
  today_index: number;
}

/** 与 Operation Brain 对齐的固定阈值 */
const THRESHOLDS = {
  commercial: 0.6,
  interaction: 0.25,
  energy: 0.7,
  trust: 0.5,
} as const;

function deriveTrustLevel(trust_ratio: number): TrustLevel {
  if (trust_ratio >= 0.7) return 'high';
  if (trust_ratio >= 0.4) return 'medium';
  return 'low';
}

function deriveInteractionLevel(interaction_ratio: number): InteractionLevel {
  if (interaction_ratio >= 0.5) return 'high';
  if (interaction_ratio > 0.25) return 'medium';
  return 'low';
}

function withLevels(
  decision: Omit<MarketingDecision, 'trust_level' | 'interaction_level'>,
  memory: CharacterMemory,
): MarketingDecision {
  return {
    ...decision,
    trust_level: deriveTrustLevel(memory.trust_ratio),
    interaction_level: deriveInteractionLevel(memory.interaction_ratio),
  };
}

function ruleRestDay(): Omit<MarketingDecision, 'trust_level' | 'interaction_level'> {
  return {
    marketing_mode: 'rest',
    promotion_level: 'none',
    recommended_ratio: 0,
    promotion_allowed: false,
    promotion_reason: ['角色今日处于高休息状态，不安排推广。'],
    marketing_priority: 100,
  };
}

function ruleHighCommercial(): Omit<MarketingDecision, 'trust_level' | 'interaction_level'> {
  return {
    marketing_mode: 'trust',
    promotion_level: 'none',
    recommended_ratio: 0.1,
    promotion_allowed: false,
    promotion_reason: ['近期商业内容占比偏高，今天回到信任建立。'],
    marketing_priority: 90,
  };
}

function ruleLowInteraction(): Omit<MarketingDecision, 'trust_level' | 'interaction_level'> {
  return {
    marketing_mode: 'interaction',
    promotion_level: 'none',
    recommended_ratio: 0.1,
    promotion_allowed: false,
    promotion_reason: ['近期互动偏低，今天优先互动而非推广。'],
    marketing_priority: 80,
  };
}

function ruleLightPromotion(): Omit<MarketingDecision, 'trust_level' | 'interaction_level'> {
  return {
    marketing_mode: 'promotion',
    promotion_level: 'light',
    recommended_ratio: 0.25,
    promotion_allowed: true,
    promotion_reason: ['状态与信任良好，且营运允许轻量推广。'],
    marketing_priority: 70,
  };
}

function ruleFallbackTrust(): Omit<MarketingDecision, 'trust_level' | 'interaction_level'> {
  return {
    marketing_mode: 'trust',
    promotion_level: 'none',
    recommended_ratio: 0.15,
    promotion_allowed: false,
    promotion_reason: ['使用基础信任营销节奏，暂不推广。'],
    marketing_priority: 50,
  };
}

/**
 * 固定优先顺序：
 * 1. High rest
 * 2. High commercial ratio
 * 3. Low interaction ratio
 * 4. High energy + high trust + business light
 * 5. Fallback trust
 */
export function evaluateMarketingRules(
  memory: CharacterMemory,
  operation: CharacterOperation,
): MarketingDecision {
  if (operation.today_rest_level === 'high') {
    return withLevels(ruleRestDay(), memory);
  }

  if (memory.commercial_ratio >= THRESHOLDS.commercial) {
    return withLevels(ruleHighCommercial(), memory);
  }

  if (memory.interaction_ratio <= THRESHOLDS.interaction) {
    return withLevels(ruleLowInteraction(), memory);
  }

  if (
    memory.energy_level >= THRESHOLDS.energy &&
    memory.trust_ratio >= THRESHOLDS.trust &&
    operation.today_business_mode === 'light'
  ) {
    return withLevels(ruleLightPromotion(), memory);
  }

  return withLevels(ruleFallbackTrust(), memory);
}
